import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Play, Filter, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MediaCard from '../components/MediaCard';
import MediaPlayer from '../components/MediaPlayer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMAGE = "https://images.unsplash.com/photo-1519491050282-cf00c82424b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxjaHVyY2h8ZW58MHx8fHwxNzc1NzQ2NzY5fDA&ixlib=rb-4.1.0&q=85";

const HomePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [media, setMedia] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [playerOpen, setPlayerOpen] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      const response = await axios.get(`${API_URL}/api/media?${params.toString()}`);
      setMedia(response.data);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  useEffect(() => {
    const category = searchParams.get('category');
    if (category) {
      setSelectedCategory(category);
    }
  }, [searchParams]);

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    if (value === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', value);
    }
    setSearchParams(searchParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMedia();
  };

  const handlePlay = (mediaItem) => {
    setSelectedMedia(mediaItem);
    setPlayerOpen(true);
  };

  const handleDownload = async (mediaItem) => {
    try {
      window.open(`${API_URL}/api/media/${mediaItem.id}/download`, '_blank');
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] md:h-[70vh] max-h-[800px] overflow-hidden">
        <img 
          src={HERO_IMAGE}
          alt="Tower Lakes Chapel"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay flex items-center justify-center">
          <div className="text-center px-6 max-w-4xl">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-4 tracking-tight">
              Tower Lakes Chapel
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Explore our collection of worship services, messages, and Bible studies
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto">
              <div className="flex gap-2 bg-white rounded-sm p-2 shadow-lg">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input 
                    type="text"
                    placeholder="Search messages, speakers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 border-0 focus-visible:ring-0 bg-transparent"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button type="submit" data-testid="hero-search-button">
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="flex-1 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h2 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">
                {selectedCategory === 'all' ? 'All Media' : categories.find(c => c.slug === selectedCategory)?.name || 'Media Library'}
              </h2>
              <p className="text-muted-foreground text-sm mt-1">
                {media.length} {media.length === 1 ? 'item' : 'items'} available
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-[180px]" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.slug}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Media Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : media.length === 0 ? (
            <div className="text-center py-20">
              <Play className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-serif text-xl font-semibold text-foreground mb-2">
                No media found
              </h3>
              <p className="text-muted-foreground">
                {searchQuery 
                  ? 'Try adjusting your search terms'
                  : 'Check back soon for new content'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="media-grid">
              {media.map((item, index) => (
                <div key={item.id} className={`stagger-${(index % 6) + 1}`}>
                  <MediaCard 
                    media={item}
                    onPlay={handlePlay}
                    onDownload={handleDownload}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* Media Player Modal */}
      <MediaPlayer 
        media={selectedMedia}
        isOpen={playerOpen}
        onClose={() => setPlayerOpen(false)}
        onDownload={handleDownload}
      />
    </div>
  );
};

export default HomePage;
